/**
 * Scenario definitions for SafePassage
 * Complete scenario set for MVP: Phase 1 (Foundation) + Phase 2 (Complexity)
 */

import type { Scenario } from './types';
import type { Phase } from '@core/types';

// ============================================================================
// PHASE 1: FOUNDATION - Controlled situations, clear signals
// ============================================================================

/**
 * Phase 1, Scenario 1: Basic Crosswalk
 * Learning focus: Always verify driver awareness before crossing
 */
const scenario1_1: Scenario = {
  id: 'p1-s1-basic-crosswalk',
  title: 'The Marked Crosswalk',
  description: 'A simple crossing at a marked crosswalk with a pedestrian signal.',
  phase: 1,
  role: 'pedestrian',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand that signals indicate right-of-way but not safety',
    'Learn to verify driver awareness before stepping into traffic',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [
          {
            id: 'vehicle-1',
            type: 'vehicle',
            spriteId: 'car-sedan-white',
            position: { x: 600, y: 400 },
            rotation: 0,
            behavior: {
              isLookingAt: { x: 0, y: 400 },
              isAwareOfPlayer: false,
              intentDescription: 'Waiting to turn right, looking left for traffic gap',
            },
          },
        ],
        signals: [
          {
            id: 'ped-signal',
            type: 'pedestrian_signal',
            position: { x: 700, y: 200 },
            state: 'walk',
            spriteId: 'signal-walk',
          },
        ],
        overlays: [],
      },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re at a crosswalk in Lahore. The pedestrian signal shows "Walk". A white car is waiting to turn right, but the driver is looking left, watching for a gap in traffic. What do you do?',
        options: [
          {
            id: 'proceed-immediately',
            label: 'Proceed into the crosswalk',
            description: 'The signal says walk, so you have the right of way',
          },
          {
            id: 'wait-eye-contact',
            label: 'Wait and make eye contact',
            description: 'Try to catch the driver\'s attention before crossing',
          },
          {
            id: 'wait-for-clear',
            label: 'Wait for the car to complete its turn',
            description: 'Let the vehicle go first to be safe',
          },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'proceed-result',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: ['rushed-crossing'],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Close Call',
        description: 'The driver started turning just as you stepped off the curb. They braked hard, stopping just in time. Both of you are shaken.',
        principle: 'Signals give you the right of way, but they don\'t guarantee other road users have seen you.',
      },
    },
    {
      id: 'eye-contact-result',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Safe Crossing',
        description: 'You raised your hand slightly and caught the driver\'s eye. They nodded and waited. You crossed safely with confidence.',
        principle: 'Eye contact with drivers confirms mutual awareness and creates a moment of communication.',
      },
    },
    {
      id: 'wait-result',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'neutral',
        title: 'Safe but Delayed',
        description: 'You waited for the car to turn. The driver never noticed you were there. You crossed safely after they left, but the signal changed and you had to wait for the next cycle.',
        principle: 'Waiting is always safe, but learning to communicate with drivers can make crossings both safe and efficient.',
      },
    },
  ],
  edges: [
    {
      id: 'edge-1',
      fromNodeId: 'start',
      toNodeId: 'proceed-result',
      decisionId: 'proceed-immediately',
      immediateFeedback: {
        severity: 'warning',
        title: 'Risky Move',
        description: 'You step into the crosswalk trusting the signal...',
      },
    },
    {
      id: 'edge-2',
      fromNodeId: 'start',
      toNodeId: 'eye-contact-result',
      decisionId: 'wait-eye-contact',
      immediateFeedback: {
        severity: 'positive',
        title: 'Good Awareness',
        description: 'You pause and look toward the driver...',
      },
    },
    {
      id: 'edge-3',
      fromNodeId: 'start',
      toNodeId: 'wait-result',
      decisionId: 'wait-for-clear',
      immediateFeedback: {
        severity: 'neutral',
        title: 'Cautious Approach',
        description: 'You decide to wait until the car has passed...',
      },
    },
  ],
};

/**
 * Phase 1, Scenario 2: Unmarked Crossing
 * Learning focus: Judging vehicle speed and distance
 */
const scenario1_2: Scenario = {
  id: 'p1-s2-unmarked-crossing',
  title: 'The Busy Road',
  description: 'Crossing a road without a marked crosswalk or signal.',
  phase: 1,
  role: 'pedestrian',
  initialNodeId: 'start',
  learningObjectives: [
    'Learn to judge vehicle speed and distance',
    'Understand that closer is not always more dangerous than faster',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: {
        backgroundId: 'urban-road-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [
          {
            id: 'vehicle-slow',
            type: 'vehicle',
            spriteId: 'car-sedan-blue',
            position: { x: 200, y: 400 },
            rotation: 0,
            speed: 20,
            behavior: {
              isAwareOfPlayer: false,
              intentDescription: 'Moving slowly, looking for parking',
            },
          },
          {
            id: 'vehicle-fast',
            type: 'vehicle',
            spriteId: 'car-suv-black',
            position: { x: 800, y: 400 },
            rotation: 180,
            speed: 60,
            behavior: {
              isAwareOfPlayer: false,
              intentDescription: 'Moving at speed limit',
            },
          },
        ],
        signals: [],
        overlays: [],
      },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You need to cross Mall Road in Lahore. There\'s no crosswalk here. A blue car is approaching slowly from your left (about 30 meters away, moving slowly). A black SUV is further away on your right (about 80 meters) but moving quickly. When do you cross?',
        options: [
          {
            id: 'cross-now',
            label: 'Cross now quickly',
            description: 'Both vehicles are far enough away',
          },
          {
            id: 'wait-blue',
            label: 'Wait for the blue car to pass',
            description: 'It\'s closer, let it go first',
          },
          {
            id: 'wait-both',
            label: 'Wait for both vehicles',
            description: 'Don\'t take any chances',
          },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'cross-now-result',
      sceneConfig: {
        backgroundId: 'urban-road-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Dangerous Situation',
        description: 'You started crossing but misjudged the SUV\'s speed. It was traveling faster than it appeared from a distance. The driver honked and swerved. You had to run the last few meters to avoid being hit.',
        principle: 'Distance alone doesn\'t determine safety. A far vehicle moving fast can reach you before a close vehicle moving slowly.',
      },
    },
    {
      id: 'wait-blue-result',
      sceneConfig: {
        backgroundId: 'urban-road-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Good Judgment',
        description: 'You let the slow blue car pass, then checked the SUV again. With a clearer view of its speed, you waited a few more seconds and crossed safely with plenty of margin.',
        principle: 'When in doubt, wait for the closer vehicle to pass. This gives you a clearer view and more time to judge remaining traffic.',
      },
    },
    {
      id: 'wait-both-result',
      sceneConfig: {
        backgroundId: 'urban-road-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'neutral',
        title: 'Safe but Slow',
        description: 'You waited for both vehicles. By the time the road was clear, more traffic had arrived. You waited through three more gaps before finding one you were comfortable with.',
        principle: 'Complete caution is safe but can lead to long waits. Learning to judge speed helps you find safe crossing opportunities more quickly.',
      },
    },
  ],
  edges: [
    {
      id: 'edge-1',
      fromNodeId: 'start',
      toNodeId: 'cross-now-result',
      decisionId: 'cross-now',
      immediateFeedback: {
        severity: 'warning',
        title: 'Taking a Risk',
        description: 'You step onto the road, watching both vehicles...',
      },
    },
    {
      id: 'edge-2',
      fromNodeId: 'start',
      toNodeId: 'wait-blue-result',
      decisionId: 'wait-blue',
      immediateFeedback: {
        severity: 'positive',
        title: 'Patience Pays',
        description: 'You wait at the curb as the blue car passes...',
      },
    },
    {
      id: 'edge-3',
      fromNodeId: 'start',
      toNodeId: 'wait-both-result',
      decisionId: 'wait-both',
      immediateFeedback: {
        severity: 'neutral',
        title: 'Maximum Caution',
        description: 'You decide to wait until the road is completely clear...',
      },
    },
  ],
};

/**
 * Phase 1, Scenario 3: The Distracted Situation
 * Learning focus: Your own attention matters as much as others'
 */
const scenario1_3: Scenario = {
  id: 'p1-s3-distraction',
  title: 'The Phone Call',
  description: 'Managing distractions while navigating traffic.',
  phase: 1,
  role: 'pedestrian',
  initialNodeId: 'start',
  learningObjectives: [
    'Recognize how personal distractions affect safety',
    'Learn to prioritize attention at critical moments',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [
          {
            id: 'ped-signal',
            type: 'pedestrian_signal',
            position: { x: 700, y: 200 },
            state: 'walk',
            spriteId: 'signal-walk',
          },
        ],
        overlays: [],
      },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re about to cross at a signal in Karachi when your phone rings. It\'s your manager calling about an urgent work matter. The walk signal just turned on. What do you do?',
        options: [
          {
            id: 'answer-cross',
            label: 'Answer and cross',
            description: 'You can do both - it\'s just walking',
          },
          {
            id: 'answer-wait',
            label: 'Answer but wait to cross',
            description: 'Take the call but stay on the curb',
          },
          {
            id: 'ignore-cross',
            label: 'Ignore the call and cross',
            description: 'Cross first, call back after',
          },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'answer-cross-result',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: ['was-distracted'],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Near Miss',
        description: 'While focused on the call, you didn\'t notice a delivery motorcycle running the red light. They swerved around you at the last moment, shouting. Your heart is racing and you\'ve lost track of what your manager was saying.',
        principle: 'Crossing the road requires your full attention. Distractions reduce your ability to notice unexpected hazards.',
      },
    },
    {
      id: 'answer-wait-result',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Balanced Approach',
        description: 'You answered the call and stepped back from the curb. You spoke briefly with your manager while waiting. When the next walk signal came, you told them to hold for 30 seconds while you crossed with full attention.',
        principle: 'Important calls can wait 30 seconds. If they can\'t wait, you shouldn\'t be crossing a road.',
      },
    },
    {
      id: 'ignore-cross-result',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Clear Priorities',
        description: 'You let the call go to voicemail and crossed with full attention. On the other side, you called back immediately. Your manager appreciated the quick response and didn\'t even notice the brief delay.',
        principle: 'Brief delays to ensure safety are almost never noticed by others. Your safety is worth those few seconds.',
      },
    },
  ],
  edges: [
    {
      id: 'edge-1',
      fromNodeId: 'start',
      toNodeId: 'answer-cross-result',
      decisionId: 'answer-cross',
      immediateFeedback: {
        severity: 'warning',
        title: 'Multitasking',
        description: 'You swipe to answer while stepping off the curb...',
      },
    },
    {
      id: 'edge-2',
      fromNodeId: 'start',
      toNodeId: 'answer-wait-result',
      decisionId: 'answer-wait',
      immediateFeedback: {
        severity: 'positive',
        title: 'Staying Put',
        description: 'You answer the call but step back from the curb...',
      },
    },
    {
      id: 'edge-3',
      fromNodeId: 'start',
      toNodeId: 'ignore-cross-result',
      decisionId: 'ignore-cross',
      immediateFeedback: {
        severity: 'positive',
        title: 'Focus First',
        description: 'You pocket your phone and look both ways...',
      },
    },
  ],
};

// ============================================================================
// PHASE 2: COMPLEXITY - Ambiguous situations, unpredictable actors
// ============================================================================

/**
 * Phase 2, Scenario 1: The Reversing Vehicle
 * Learning focus: Recognizing vehicle intentions from behavior cues
 */
const scenario2_1: Scenario = {
  id: 'p2-s1-reversing-vehicle',
  title: 'The Parking Lot Exit',
  description: 'Navigating around a vehicle that may be reversing.',
  phase: 2,
  role: 'pedestrian',
  initialNodeId: 'start',
  learningObjectives: [
    'Recognize reverse lights and vehicle movement cues',
    'Understand that parked vehicles can become moving hazards',
  ],
  estimatedDuration: 150,
  nodes: [
    {
      id: 'start',
      sceneConfig: {
        backgroundId: 'parking-lot-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [
          {
            id: 'vehicle-1',
            type: 'vehicle',
            spriteId: 'car-suv-silver',
            position: { x: 400, y: 350 },
            rotation: 90,
            behavior: {
              isAwareOfPlayer: false,
              intentDescription: 'Reverse lights on, about to back out',
            },
          },
        ],
        signals: [],
        overlays: [],
      },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re walking through a parking lot near Centaurus Mall in Islamabad. A silver SUV in a parking space has its reverse lights on, and you can see exhaust from the tailpipe. The driver is looking over their shoulder but hasn\'t seen you. You need to pass behind this vehicle to reach your car.',
        options: [
          {
            id: 'walk-behind',
            label: 'Walk behind quickly',
            description: 'You can make it before they start moving',
          },
          {
            id: 'walk-front',
            label: 'Walk around the front instead',
            description: 'Go the long way to avoid the reversing path',
          },
          {
            id: 'wait-signal',
            label: 'Wait and signal to the driver',
            description: 'Get their attention before passing',
          },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'walk-behind-result',
      sceneConfig: {
        backgroundId: 'parking-lot-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Close Call',
        description: 'The SUV started reversing just as you stepped behind it. You had to jump back quickly to avoid being hit. The driver braked hard and looked shocked—they never saw you coming.',
        principle: 'Never walk behind a vehicle with reverse lights on. The driver\'s view behind them is limited, and they may move at any moment.',
      },
    },
    {
      id: 'walk-front-result',
      sceneConfig: {
        backgroundId: 'parking-lot-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Safe Detour',
        description: 'You walked around the front of the SUV. As you passed, you saw it reverse out of the space. The driver never knew you were there, but you knew exactly where they were.',
        principle: 'When a vehicle shows signs of moving, give it space. A few extra steps are always worth the safety.',
      },
    },
    {
      id: 'wait-signal-result',
      sceneConfig: {
        backgroundId: 'parking-lot-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Communication Works',
        description: 'You waved to catch the driver\'s eye in their mirror. They saw you, nodded, and waited while you passed behind. Both of you continued safely.',
        principle: 'Making yourself visible to drivers eliminates uncertainty. A moment of communication prevents accidents.',
      },
    },
  ],
  edges: [
    {
      id: 'edge-1',
      fromNodeId: 'start',
      toNodeId: 'walk-behind-result',
      decisionId: 'walk-behind',
      immediateFeedback: {
        severity: 'warning',
        title: 'Risky Path',
        description: 'You step behind the SUV with reverse lights on...',
      },
    },
    {
      id: 'edge-2',
      fromNodeId: 'start',
      toNodeId: 'walk-front-result',
      decisionId: 'walk-front',
      immediateFeedback: {
        severity: 'positive',
        title: 'Smart Detour',
        description: 'You choose to walk around the front...',
      },
    },
    {
      id: 'edge-3',
      fromNodeId: 'start',
      toNodeId: 'wait-signal-result',
      decisionId: 'wait-signal',
      immediateFeedback: {
        severity: 'positive',
        title: 'Getting Attention',
        description: 'You wave to catch the driver\'s eye...',
      },
    },
  ],
};

/**
 * Phase 2, Scenario 2: The Multiple Lane Crossing
 * Learning focus: Partial visibility and the "wave of death"
 */
const scenario2_2: Scenario = {
  id: 'p2-s2-multiple-lanes',
  title: 'The Stopped Traffic',
  description: 'Crossing when one lane stops but others may not.',
  phase: 2,
  role: 'pedestrian',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand the danger of obscured lanes',
    'Learn why a stopped vehicle doesn\'t mean all traffic has stopped',
  ],
  estimatedDuration: 150,
  nodes: [
    {
      id: 'start',
      sceneConfig: {
        backgroundId: 'multi-lane-road-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [
          {
            id: 'vehicle-stopped',
            type: 'vehicle',
            spriteId: 'car-sedan-red',
            position: { x: 300, y: 400 },
            rotation: 0,
            behavior: {
              isAwareOfPlayer: true,
              intentDescription: 'Stopped and waving you to cross',
            },
          },
        ],
        signals: [],
        overlays: [],
      },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re crossing Shahrah-e-Faisal in Karachi, a multi-lane road. A car in the near lane has stopped and the driver is waving you to cross. But you can\'t see the far lane clearly because of the stopped car. There\'s no signal here.',
        options: [
          {
            id: 'cross-waved',
            label: 'Cross since the driver waved',
            description: 'They stopped for you, so cross quickly',
          },
          {
            id: 'peek-around',
            label: 'Peek around the car first',
            description: 'Check the other lane before crossing fully',
          },
          {
            id: 'wait-clear',
            label: 'Wait until you can see all lanes',
            description: 'Don\'t cross until you have full visibility',
          },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'cross-waved-result',
      sceneConfig: {
        backgroundId: 'multi-lane-road-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'The Hidden Lane',
        description: 'You stepped past the stopped car and a motorcycle in the far lane nearly hit you. The rider swerved and fell. The driver who waved meant well but couldn\'t see the other lane either.',
        principle: 'Never rely on one driver\'s wave to cross multiple lanes. They can only see their lane—other drivers don\'t know you\'re there.',
      },
    },
    {
      id: 'peek-around-result',
      sceneConfig: {
        backgroundId: 'multi-lane-road-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Careful Check',
        description: 'You walked to the edge of the stopped car and looked into the far lane. A bus was approaching fast. You waited for it to pass, thanked the car driver, and crossed safely.',
        principle: 'Always check each lane individually. One stopped vehicle tells you nothing about the others.',
      },
    },
    {
      id: 'wait-clear-result',
      sceneConfig: {
        backgroundId: 'multi-lane-road-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'neutral',
        title: 'Patient Approach',
        description: 'You waved "no thanks" to the stopped driver and waited. They drove off, confused but not offended. A minute later, all lanes were clear and you crossed with full visibility.',
        principle: 'It\'s okay to decline a driver\'s gesture if you can\'t verify it\'s safe. Your safety comes before social politeness.',
      },
    },
  ],
  edges: [
    {
      id: 'edge-1',
      fromNodeId: 'start',
      toNodeId: 'cross-waved-result',
      decisionId: 'cross-waved',
      immediateFeedback: {
        severity: 'warning',
        title: 'Trusting the Wave',
        description: 'You start to cross, trusting the driver\'s gesture...',
      },
    },
    {
      id: 'edge-2',
      fromNodeId: 'start',
      toNodeId: 'peek-around-result',
      decisionId: 'peek-around',
      immediateFeedback: {
        severity: 'positive',
        title: 'Verifying First',
        description: 'You edge forward to check the hidden lane...',
      },
    },
    {
      id: 'edge-3',
      fromNodeId: 'start',
      toNodeId: 'wait-clear-result',
      decisionId: 'wait-clear',
      immediateFeedback: {
        severity: 'neutral',
        title: 'Declining the Wave',
        description: 'You choose to wait for full visibility...',
      },
    },
  ],
};

/**
 * Phase 2, Scenario 3: The Weather Condition
 * Learning focus: Environmental factors affecting visibility and stopping distance
 */
const scenario2_3: Scenario = {
  id: 'p2-s3-weather',
  title: 'The Rainy Evening',
  description: 'Crossing in poor visibility conditions.',
  phase: 2,
  role: 'pedestrian',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand how rain affects driver visibility',
    'Learn that stopping distances increase on wet roads',
  ],
  estimatedDuration: 150,
  nodes: [
    {
      id: 'start',
      sceneConfig: {
        backgroundId: 'urban-road-dusk',
        timeOfDay: 'dusk',
        weather: 'rain',
        actors: [
          {
            id: 'vehicle-approaching',
            type: 'vehicle',
            spriteId: 'car-sedan-dark',
            position: { x: 600, y: 400 },
            rotation: 180,
            behavior: {
              isAwareOfPlayer: false,
              intentDescription: 'Driving with headlights, wipers on',
            },
          },
        ],
        signals: [],
        overlays: [],
      },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'It\'s raining in Peshawar and getting dark. You need to cross to catch your bus. The pedestrian crossing has no signal. A car is approaching with headlights on, about 50 meters away. You\'re wearing dark clothes.',
        options: [
          {
            id: 'cross-normal',
            label: 'Cross at normal pace',
            description: 'The car is far enough away',
          },
          {
            id: 'cross-visible',
            label: 'Make yourself visible first',
            description: 'Use your phone light or wave before crossing',
          },
          {
            id: 'find-better',
            label: 'Find a better-lit crossing point',
            description: 'Walk to the street light 30 meters away',
          },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'cross-normal-result',
      sceneConfig: {
        backgroundId: 'urban-road-dusk',
        timeOfDay: 'dusk',
        weather: 'rain',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Invisible Pedestrian',
        description: 'The driver didn\'t see you until the last moment. They braked hard but skidded on the wet road, stopping just inches away. You could see the fear in their eyes—and feel it in your chest.',
        principle: 'In rain and darkness, drivers\' visibility is severely reduced. Dark clothing makes you nearly invisible. Never assume they can see you.',
      },
    },
    {
      id: 'cross-visible-result',
      sceneConfig: {
        backgroundId: 'urban-road-dusk',
        timeOfDay: 'dusk',
        weather: 'rain',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Visible and Safe',
        description: 'You turned on your phone flashlight and waved it. The driver slowed down noticeably and flashed their headlights to acknowledge you. You crossed safely.',
        principle: 'Making yourself visible in poor conditions is your responsibility. Any light source helps drivers see you earlier.',
      },
    },
    {
      id: 'find-better-result',
      sceneConfig: {
        backgroundId: 'urban-road-dusk',
        timeOfDay: 'dusk',
        weather: 'rain',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Smart Relocation',
        description: 'You walked to the street light. Under its glow, drivers could clearly see you waiting. You crossed easily when there was a gap, and still caught your bus.',
        principle: 'In poor visibility, crossing under a light source can make the difference between being seen and being hit.',
      },
    },
  ],
  edges: [
    {
      id: 'edge-1',
      fromNodeId: 'start',
      toNodeId: 'cross-normal-result',
      decisionId: 'cross-normal',
      immediateFeedback: {
        severity: 'warning',
        title: 'Into the Dark',
        description: 'You step onto the wet road in your dark clothes...',
      },
    },
    {
      id: 'edge-2',
      fromNodeId: 'start',
      toNodeId: 'cross-visible-result',
      decisionId: 'cross-visible',
      immediateFeedback: {
        severity: 'positive',
        title: 'Lighting Up',
        description: 'You activate your phone light to signal your presence...',
      },
    },
    {
      id: 'edge-3',
      fromNodeId: 'start',
      toNodeId: 'find-better-result',
      decisionId: 'find-better',
      immediateFeedback: {
        severity: 'positive',
        title: 'Finding Light',
        description: 'You walk toward the street light...',
      },
    },
  ],
};

// ============================================================================
// PHASE 3: INTEGRATION - Compound situations, multiple decision points
// ============================================================================

/**
 * Phase 3, Scenario 1: The School Zone Rush
 * Learning focus: Managing multiple hazards simultaneously
 */
const scenario3_1: Scenario = {
  id: 'p3-s1-school-zone',
  title: 'The School Rush',
  description: 'Navigating a chaotic school pickup zone with multiple hazards.',
  phase: 3,
  role: 'pedestrian',
  initialNodeId: 'start',
  learningObjectives: [
    'Manage multiple simultaneous hazards',
    'Prioritize threats when attention is divided',
  ],
  estimatedDuration: 180,
  nodes: [
    {
      id: 'start',
      sceneConfig: {
        backgroundId: 'school-zone-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [
          {
            id: 'children',
            type: 'pedestrian',
            spriteId: 'group-children',
            position: { x: 300, y: 400 },
            rotation: 0,
          },
          {
            id: 'double-parked',
            type: 'vehicle',
            spriteId: 'car-double-parked',
            position: { x: 500, y: 380 },
            rotation: 0,
          },
        ],
        signals: [],
        overlays: [],
      },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re walking past a school in Faisalabad at pickup time. The road is chaotic: cars are double-parked, children are running between vehicles, and drivers are distracted watching for their kids. You need to cross to reach the pharmacy. A group of children is running toward the road to your left, and a car is trying to pull out of a tight spot to your right.',
        options: [
          {
            id: 'cross-quickly',
            label: 'Cross quickly now',
            description: 'Get across before more chaos develops',
          },
          {
            id: 'watch-children',
            label: 'Wait and watch the children first',
            description: 'Make sure they\'re safe before you move',
          },
          {
            id: 'walk-to-crossing',
            label: 'Walk to the marked crossing',
            description: 'Use the school crossing 20 meters away',
          },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'cross-quickly-result',
      sceneConfig: {
        backgroundId: 'school-zone-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: ['rushed-school-zone'],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Too Many Variables',
        description: 'You started crossing, but a child suddenly darted in front of you. You stopped to avoid them, and the car pulling out nearly hit you from behind because the driver was watching the children, not you.',
        principle: 'In chaotic environments, the safest path is often to slow down, not speed up. Rushing adds you as another unpredictable element.',
      },
    },
    {
      id: 'watch-children-result',
      sceneConfig: {
        backgroundId: 'school-zone-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'neutral',
        title: 'Protective Instinct',
        description: 'You watched as a parent caught the running children before they reached the road. But while you watched them, the car finished pulling out and blocked your crossing path. You waited another two minutes for a gap.',
        principle: 'It\'s natural to worry about children, but in chaotic zones, you need to prioritize your own safe path first.',
      },
    },
    {
      id: 'walk-to-crossing-result',
      sceneConfig: {
        backgroundId: 'school-zone-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Finding Order',
        description: 'You walked to the marked school crossing where a guard was managing traffic. Cars were stopping properly, and you crossed in an orderly line with other parents. The chaos was just 20 meters away, but here it was calm.',
        principle: 'In chaotic environments, seek out controlled crossing points. A small detour to a marked crossing is safer than navigating unpredictable traffic.',
      },
    },
  ],
  edges: [
    {
      id: 'edge-1',
      fromNodeId: 'start',
      toNodeId: 'cross-quickly-result',
      decisionId: 'cross-quickly',
      immediateFeedback: {
        severity: 'warning',
        title: 'Into the Chaos',
        description: 'You step into the chaotic school zone traffic...',
      },
    },
    {
      id: 'edge-2',
      fromNodeId: 'start',
      toNodeId: 'watch-children-result',
      decisionId: 'watch-children',
      immediateFeedback: {
        severity: 'neutral',
        title: 'Watching Others',
        description: 'You pause to watch the children near the road...',
      },
    },
    {
      id: 'edge-3',
      fromNodeId: 'start',
      toNodeId: 'walk-to-crossing-result',
      decisionId: 'walk-to-crossing',
      immediateFeedback: {
        severity: 'positive',
        title: 'Seeking Safety',
        description: 'You walk toward the marked school crossing...',
      },
    },
  ],
};

/**
 * Phase 3, Scenario 2: The Construction Zone
 * Learning focus: Adapting to temporary and unusual road conditions
 */
const scenario3_2: Scenario = {
  id: 'p3-s2-construction',
  title: 'The Construction Detour',
  description: 'Navigating changed road conditions and temporary paths.',
  phase: 3,
  role: 'pedestrian',
  initialNodeId: 'start',
  learningObjectives: [
    'Adapt to changed and temporary road conditions',
    'Recognize construction zone hazards',
  ],
  estimatedDuration: 180,
  nodes: [
    {
      id: 'start',
      sceneConfig: {
        backgroundId: 'construction-zone-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'Your usual route in Rawalpindi is blocked by construction. The sidewalk is closed and pedestrians are being diverted onto a narrow temporary path alongside moving traffic. There\'s no barrier between the path and the road. Construction vehicles are moving in and out. You see other pedestrians just walking in the road to avoid the narrow path.',
        options: [
          {
            id: 'use-path',
            label: 'Use the narrow temporary path',
            description: 'Stay on the designated pedestrian route',
          },
          {
            id: 'walk-road',
            label: 'Walk in the road like others',
            description: 'Follow what other pedestrians are doing',
          },
          {
            id: 'find-alternate',
            label: 'Find an alternate route',
            description: 'Take the longer way around the construction',
          },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'use-path-result',
      sceneConfig: {
        backgroundId: 'construction-zone-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'neutral',
        title: 'Careful Progress',
        description: 'The path was tight and uncomfortable. You had to press against the temporary fence when a truck passed close by. But you made it through safely by staying alert and moving steadily.',
        principle: 'Designated paths exist for a reason, even when they\'re inconvenient. Construction zones have planned safe routes—use them.',
      },
    },
    {
      id: 'walk-road-result',
      sceneConfig: {
        backgroundId: 'construction-zone-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Mixing with Traffic',
        description: 'A construction truck backing up didn\'t see you among the other pedestrians in the road. The warning beeps were drowned out by other noise. You had to scramble out of the way, nearly falling.',
        principle: 'Following what others do doesn\'t make it safe. Construction zones have heavy vehicles with limited visibility—stay in designated areas.',
      },
    },
    {
      id: 'find-alternate-result',
      sceneConfig: {
        backgroundId: 'construction-zone-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Longer but Safer',
        description: 'You walked two blocks around the construction zone. It added five minutes to your journey, but you stayed on proper sidewalks the entire way, away from heavy equipment and traffic chaos.',
        principle: 'When conditions are significantly compromised, changing your route entirely is often safer than navigating hazards.',
      },
    },
  ],
  edges: [
    {
      id: 'edge-1',
      fromNodeId: 'start',
      toNodeId: 'use-path-result',
      decisionId: 'use-path',
      immediateFeedback: {
        severity: 'neutral',
        title: 'Taking the Path',
        description: 'You step onto the narrow temporary walkway...',
      },
    },
    {
      id: 'edge-2',
      fromNodeId: 'start',
      toNodeId: 'walk-road-result',
      decisionId: 'walk-road',
      immediateFeedback: {
        severity: 'warning',
        title: 'Following the Crowd',
        description: 'You step into the road with other pedestrians...',
      },
    },
    {
      id: 'edge-3',
      fromNodeId: 'start',
      toNodeId: 'find-alternate-result',
      decisionId: 'find-alternate',
      immediateFeedback: {
        severity: 'positive',
        title: 'Finding Another Way',
        description: 'You turn back to find an alternate route...',
      },
    },
  ],
};

/**
 * Phase 3, Scenario 3: The Emergency Vehicle
 * Learning focus: Responding to unexpected priority situations
 */
const scenario3_3: Scenario = {
  id: 'p3-s3-emergency',
  title: 'The Ambulance',
  description: 'Responding to an approaching emergency vehicle while crossing.',
  phase: 3,
  role: 'pedestrian',
  initialNodeId: 'start',
  learningObjectives: [
    'Respond appropriately to emergency vehicles',
    'Understand that emergency situations change normal rules',
  ],
  estimatedDuration: 150,
  nodes: [
    {
      id: 'start',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [
          {
            id: 'ped-signal',
            type: 'pedestrian_signal',
            position: { x: 700, y: 200 },
            state: 'walk',
            spriteId: 'signal-walk',
          },
        ],
        overlays: [],
      },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re halfway across a busy intersection in Multan. You have the walk signal. Suddenly you hear a siren—an ambulance is approaching fast from behind, and traffic is starting to pull aside. Cars that were stopped are now moving unpredictably to make way. The ambulance is getting louder.',
        options: [
          {
            id: 'continue-crossing',
            label: 'Continue crossing quickly',
            description: 'You have the signal, finish crossing fast',
          },
          {
            id: 'stop-middle',
            label: 'Stop where you are',
            description: 'Stay still until the ambulance passes',
          },
          {
            id: 'return-start',
            label: 'Go back to where you started',
            description: 'Return to the curb you came from',
          },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'continue-crossing-result',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'In the Path',
        description: 'A car that pulled over to let the ambulance pass moved directly into your path. You had to jump back. The ambulance passed, but you ended up stuck in the middle of the intersection with traffic resuming chaotically around you.',
        principle: 'When emergency vehicles approach, normal traffic patterns break down. Your walk signal means nothing when drivers are focused on the siren.',
      },
    },
    {
      id: 'stop-middle-result',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Being Predictable',
        description: 'You stopped and stood still. Cars maneuvered around you as they made way for the ambulance. The ambulance passed safely, drivers could see you and predict your position, and then you finished crossing.',
        principle: 'In emergency situations, being stationary and predictable is often safer than moving. Drivers can plan around a still object.',
      },
    },
    {
      id: 'return-start-result',
      sceneConfig: {
        backgroundId: 'urban-intersection-day',
        timeOfDay: 'day',
        weather: 'clear',
        actors: [],
        signals: [],
        overlays: [],
      },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'neutral',
        title: 'Retreating Safely',
        description: 'You hurried back to the curb. The ambulance passed through, and traffic resumed. But when you tried to cross again, your signal had changed and you had to wait for the next cycle.',
        principle: 'Returning to safety is valid, but if you\'re already halfway across, staying still is often simpler than retreating.',
      },
    },
  ],
  edges: [
    {
      id: 'edge-1',
      fromNodeId: 'start',
      toNodeId: 'continue-crossing-result',
      decisionId: 'continue-crossing',
      immediateFeedback: {
        severity: 'warning',
        title: 'Pushing Through',
        description: 'You speed up, trying to finish crossing...',
      },
    },
    {
      id: 'edge-2',
      fromNodeId: 'start',
      toNodeId: 'stop-middle-result',
      decisionId: 'stop-middle',
      immediateFeedback: {
        severity: 'positive',
        title: 'Standing Still',
        description: 'You stop and stand still in the intersection...',
      },
    },
    {
      id: 'edge-3',
      fromNodeId: 'start',
      toNodeId: 'return-start-result',
      decisionId: 'return-start',
      immediateFeedback: {
        severity: 'neutral',
        title: 'Heading Back',
        description: 'You turn and head back to the curb...',
      },
    },
  ],
};

// ============================================================================
// CYCLIST SCENARIOS - PHASE 1: FOUNDATION
// ============================================================================

const cyclist1_1: Scenario = {
  id: 'cyclist-p1-s1-bike-lane',
  title: 'The Bike Lane',
  description: 'Riding in a dedicated bike lane alongside traffic.',
  phase: 1,
  role: 'cyclist',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand bike lane positioning and visibility',
    'Learn to anticipate car doors and merging vehicles',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re cycling in a bike lane on Shahrah-e-Faisal, Karachi. A row of parked cars is on your right. A white sedan ahead has its brake lights on and someone inside. What do you do?',
        options: [
          { id: 'maintain-speed', label: 'Maintain speed in the bike lane', description: 'Stay in your lane and keep moving' },
          { id: 'slow-check', label: 'Slow down and watch the car', description: 'Reduce speed and be ready to react' },
          { id: 'move-left', label: 'Move left into traffic lane', description: 'Leave the bike lane to pass safely' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'maintain-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: ['door-zone-risk'],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Door Zone Danger',
        description: 'The car door swung open suddenly. You had no time to react and collided with it.',
        principle: 'The "door zone" extends about 1 meter from parked cars. When you see brake lights or movement inside, always slow down or move left.',
      },
    },
    {
      id: 'slow-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Alert Cycling',
        description: 'The door opened, but you had already slowed down. You stopped safely and waited for the passenger to exit.',
        principle: 'Brake lights and movement inside parked cars are warning signs. Slowing down gives you time to react.',
      },
    },
    {
      id: 'move-left-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Proactive Safety',
        description: 'You moved left after checking traffic. The door opened just as you passed, but you were safely out of range.',
        principle: 'Sometimes leaving the bike lane temporarily is the safest choice. Always check behind before merging.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'maintain-result', decisionId: 'maintain-speed', immediateFeedback: { severity: 'warning', title: 'Staying Course', description: 'You continue at speed...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'slow-result', decisionId: 'slow-check', immediateFeedback: { severity: 'positive', title: 'Slowing Down', description: 'You ease off and watch carefully...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'move-left-result', decisionId: 'move-left', immediateFeedback: { severity: 'positive', title: 'Changing Position', description: 'You check and move left...' } },
  ],
};

const cyclist1_2: Scenario = {
  id: 'cyclist-p1-s2-intersection',
  title: 'The Right Turn',
  description: 'Approaching an intersection where cars may turn across your path.',
  phase: 1,
  role: 'cyclist',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand blind spots at intersections',
    'Learn to make yourself visible to turning drivers',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re approaching an intersection in Lahore. A car beside you has its right indicator on. The light is green. What do you do?',
        options: [
          { id: 'speed-ahead', label: 'Speed up to pass before they turn', description: 'Get ahead of the car quickly' },
          { id: 'slow-behind', label: 'Slow down and let them turn first', description: 'Stay behind the turning vehicle' },
          { id: 'hold-position', label: 'Maintain speed alongside', description: 'Keep your current position and speed' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'speed-result',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Risky Racing',
        description: 'You accelerated hard and just barely got ahead. The driver had to brake suddenly and honked angrily.',
        principle: 'Racing cars to intersections is unpredictable. The driver may not see you accelerating in their blind spot.',
      },
    },
    {
      id: 'slow-result',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Patience Pays',
        description: 'You slowed and let the car turn. The driver waved a thank you and you continued safely through the intersection.',
        principle: 'When a car signals to turn across your path, it\'s usually safest to let them go first. A few seconds of patience prevents conflicts.',
      },
    },
    {
      id: 'hold-result',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Collision Course',
        description: 'The driver began turning without seeing you. You had to swerve and nearly fell.',
        principle: 'Staying alongside a turning vehicle puts you in their blind spot. Always adjust your position when you see turn signals.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'speed-result', decisionId: 'speed-ahead', immediateFeedback: { severity: 'warning', title: 'Accelerating', description: 'You pedal hard to get ahead...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'slow-result', decisionId: 'slow-behind', immediateFeedback: { severity: 'positive', title: 'Yielding', description: 'You ease back and let them go...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'hold-result', decisionId: 'hold-position', immediateFeedback: { severity: 'warning', title: 'Holding Position', description: 'You continue alongside...' } },
  ],
};

const cyclist1_3: Scenario = {
  id: 'cyclist-p1-s3-visibility',
  title: 'The Evening Ride',
  description: 'Cycling at dusk with decreasing visibility.',
  phase: 1,
  role: 'cyclist',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand the importance of lights and reflective gear',
    'Learn how visibility affects driver awareness',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-road-dusk', timeOfDay: 'dusk', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'It\'s getting dark in Islamabad. You\'re cycling home but your lights aren\'t working. A busy main road lies ahead. What do you do?',
        options: [
          { id: 'continue-dark', label: 'Continue on the main road', description: 'It\'s not fully dark yet, you can make it' },
          { id: 'side-streets', label: 'Take quieter side streets', description: 'Less traffic means less risk' },
          { id: 'walk-bike', label: 'Walk your bike on the footpath', description: 'Become a pedestrian until you reach home' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'continue-result',
      sceneConfig: { backgroundId: 'urban-road-dusk', timeOfDay: 'dusk', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Invisible Cyclist',
        description: 'A car changed lanes without seeing you. You had to brake hard and fell on the road.',
        principle: 'Without lights, you\'re nearly invisible to drivers at dusk. This is when most cycling accidents occur.',
      },
    },
    {
      id: 'side-result',
      sceneConfig: { backgroundId: 'urban-road-dusk', timeOfDay: 'dusk', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Safer Route',
        description: 'The side streets were quieter but poorly lit. You made it home but had a few close calls at intersections.',
        principle: 'Side streets reduce exposure but don\'t eliminate the risk. Without lights, any road is dangerous after dusk.',
      },
    },
    {
      id: 'walk-result',
      sceneConfig: { backgroundId: 'urban-road-dusk', timeOfDay: 'dusk', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Smart Decision',
        description: 'Walking your bike was slower but completely safe. You used the footpath and pedestrian crossings.',
        principle: 'When your bike isn\'t safe to ride, becoming a pedestrian is always an option. Safety is more important than speed.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'continue-result', decisionId: 'continue-dark', immediateFeedback: { severity: 'warning', title: 'Pressing On', description: 'You continue into the dimming light...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'side-result', decisionId: 'side-streets', immediateFeedback: { severity: 'neutral', title: 'Alternate Route', description: 'You turn onto quieter streets...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'walk-result', decisionId: 'walk-bike', immediateFeedback: { severity: 'positive', title: 'Walking Home', description: 'You dismount and walk...' } },
  ],
};

// ============================================================================
// CYCLIST SCENARIOS - PHASE 2: COMPLEXITY
// ============================================================================

const cyclist2_1: Scenario = {
  id: 'cyclist-p2-s1-roundabout',
  title: 'The Roundabout',
  description: 'Navigating a busy roundabout with multiple lanes.',
  phase: 2,
  role: 'cyclist',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand lane positioning in roundabouts',
    'Learn to signal intentions clearly to drivers',
  ],
  estimatedDuration: 150,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'roundabout-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re approaching a roundabout in Faisalabad. You need to take the third exit. Traffic is moderate. How do you position yourself?',
        options: [
          { id: 'stay-left', label: 'Stay in the left lane throughout', description: 'Hug the outside edge of the roundabout' },
          { id: 'take-lane', label: 'Take the center of your lane', description: 'Make yourself visible and claim space' },
          { id: 'dismount', label: 'Dismount and use crossings', description: 'Walk across as a pedestrian' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'left-result',
      sceneConfig: { backgroundId: 'roundabout-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Cut Off',
        description: 'A car exiting at the second exit cut across you. You had to brake hard to avoid collision.',
        principle: 'Hugging the left edge puts you in conflict with exiting vehicles. In roundabouts, taking the lane is often safer.',
      },
    },
    {
      id: 'lane-result',
      sceneConfig: { backgroundId: 'roundabout-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Clear Positioning',
        description: 'Drivers saw you clearly and gave you space. You signaled before your exit and left safely.',
        principle: 'Taking the lane in a roundabout makes you visible and predictable. Signal clearly when exiting.',
      },
    },
    {
      id: 'dismount-result',
      sceneConfig: { backgroundId: 'roundabout-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Pedestrian Mode',
        description: 'You walked around using the pedestrian crossings. Slower, but completely safe.',
        principle: 'For complex junctions, walking your bike is always a valid safe option, especially when traffic is heavy.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'left-result', decisionId: 'stay-left', immediateFeedback: { severity: 'warning', title: 'Hugging Left', description: 'You stay close to the edge...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'lane-result', decisionId: 'take-lane', immediateFeedback: { severity: 'positive', title: 'Taking Position', description: 'You move to lane center...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'dismount-result', decisionId: 'dismount', immediateFeedback: { severity: 'neutral', title: 'Dismounting', description: 'You get off and walk...' } },
  ],
};

const cyclist2_2: Scenario = {
  id: 'cyclist-p2-s2-bus',
  title: 'The Bus Stop',
  description: 'Passing a bus that has stopped to pick up passengers.',
  phase: 2,
  role: 'cyclist',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand risks around buses and large vehicles',
    'Learn to anticipate pedestrian movement',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'A bus has stopped ahead on Mall Road, Lahore. Passengers are getting off. You can see a gap in traffic on your right. What do you do?',
        options: [
          { id: 'pass-right', label: 'Pass the bus on the right', description: 'Use the traffic lane to overtake' },
          { id: 'wait-behind', label: 'Wait behind the bus', description: 'Stay until the bus moves on' },
          { id: 'pass-left', label: 'Pass between bus and curb', description: 'Squeeze through on the left' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'right-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Clean Overtake',
        description: 'You checked traffic, signaled, and passed the bus safely on the right. You rejoined the bike lane ahead.',
        principle: 'Passing stationary buses on the right (traffic side) is usually safest as you\'re visible to drivers and away from passengers.',
      },
    },
    {
      id: 'wait-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'neutral',
        title: 'Patient Waiting',
        description: 'You waited for the bus to leave. It took a minute but you avoided all risks.',
        principle: 'Waiting is always safe, though sometimes passing carefully on the traffic side is more practical.',
      },
    },
    {
      id: 'left-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Hidden Hazard',
        description: 'A passenger stepped off just as you were passing. You collided and both fell.',
        principle: 'Never pass a bus on the left (pavement side) when passengers are boarding or alighting. You cannot see who might step out.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'right-result', decisionId: 'pass-right', immediateFeedback: { severity: 'positive', title: 'Moving Right', description: 'You check and move into traffic...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'wait-result', decisionId: 'wait-behind', immediateFeedback: { severity: 'neutral', title: 'Waiting', description: 'You stop and wait...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'left-result', decisionId: 'pass-left', immediateFeedback: { severity: 'warning', title: 'Squeezing Through', description: 'You try to pass on the left...' } },
  ],
};

const cyclist2_3: Scenario = {
  id: 'cyclist-p2-s3-wet-road',
  title: 'The Wet Road',
  description: 'Cycling on wet roads after rain.',
  phase: 2,
  role: 'cyclist',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand how wet conditions affect braking',
    'Learn to adjust speed and technique for weather',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-road-rain', timeOfDay: 'day', weather: 'rain', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'It has been raining in Rawalpindi. The road is wet with puddles. You\'re approaching a downhill section with a junction at the bottom. What do you do?',
        options: [
          { id: 'normal-speed', label: 'Continue at normal speed', description: 'You\'ll brake when you need to' },
          { id: 'early-braking', label: 'Reduce speed early and brake gently', description: 'Start slowing well before the junction' },
          { id: 'dismount-walk', label: 'Dismount and walk down', description: 'Walk your bike down the hill' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'normal-result',
      sceneConfig: { backgroundId: 'urban-road-rain', timeOfDay: 'day', weather: 'rain', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Lost Grip',
        description: 'When you braked at the junction, your wheels locked and slid. You fell on the wet road.',
        principle: 'Wet roads dramatically increase stopping distance. Brakes on wet rims are far less effective.',
      },
    },
    {
      id: 'early-result',
      sceneConfig: { backgroundId: 'urban-road-rain', timeOfDay: 'day', weather: 'rain', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Controlled Descent',
        description: 'By braking early and gently, you dried your rims and slowed gradually. You stopped safely at the junction.',
        principle: 'In wet conditions, brake early and gently. Light braking helps dry the rims and gives you control.',
      },
    },
    {
      id: 'walk-result',
      sceneConfig: { backgroundId: 'urban-road-rain', timeOfDay: 'day', weather: 'rain', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Safest Choice',
        description: 'Walking down eliminated all risk of sliding. You reached the bottom safely and continued riding.',
        principle: 'If conditions feel dangerous, walking is always an option. There\'s no shame in being careful.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'normal-result', decisionId: 'normal-speed', immediateFeedback: { severity: 'warning', title: 'Speeding Down', description: 'You roll down the hill...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'early-result', decisionId: 'early-braking', immediateFeedback: { severity: 'positive', title: 'Slowing Early', description: 'You begin braking gently...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'walk-result', decisionId: 'dismount-walk', immediateFeedback: { severity: 'neutral', title: 'Walking Down', description: 'You get off and walk...' } },
  ],
};

// ============================================================================
// CYCLIST SCENARIOS - PHASE 3: INTEGRATION
// ============================================================================

const cyclist3_1: Scenario = {
  id: 'cyclist-p3-s1-truck',
  title: 'The Turning Truck',
  description: 'A large truck is turning at an intersection ahead.',
  phase: 3,
  role: 'cyclist',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand blind spots of large vehicles',
    'Learn the danger of being alongside turning trucks',
  ],
  estimatedDuration: 150,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re at a red light in Karachi. A large truck is in the lane beside you, indicating left. When the light turns green, what do you do?',
        options: [
          { id: 'go-first', label: 'Accelerate to get ahead of the truck', description: 'You\'re faster off the line' },
          { id: 'wait-behind', label: 'Wait and let the truck go first', description: 'Let it complete its turn' },
          { id: 'go-alongside', label: 'Go straight alongside the truck', description: 'You\'re going straight, they\'re turning' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'first-result',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Close Call',
        description: 'You sprinted ahead but the truck was faster than expected. You barely cleared its path.',
        principle: 'Never race large vehicles. They accelerate faster than you think and their drivers cannot see you.',
      },
    },
    {
      id: 'behind-result',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Safe Distance',
        description: 'The truck turned wide, using both lanes. By waiting, you avoided being caught in its path.',
        principle: 'Large vehicles turn wide and have huge blind spots. Always wait and let them complete their turn.',
      },
    },
    {
      id: 'alongside-result',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Blind Spot',
        description: 'The truck began turning. The driver couldn\'t see you. You were struck by the side of the trailer.',
        principle: 'Trucks have massive blind spots on their left. Being alongside a turning truck is one of the most dangerous positions.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'first-result', decisionId: 'go-first', immediateFeedback: { severity: 'warning', title: 'Sprinting', description: 'You pedal hard to get ahead...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'behind-result', decisionId: 'wait-behind', immediateFeedback: { severity: 'positive', title: 'Waiting Back', description: 'You hold position and wait...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'alongside-result', decisionId: 'go-alongside', immediateFeedback: { severity: 'warning', title: 'Going Straight', description: 'You start moving forward...' } },
  ],
};

const cyclist3_2: Scenario = {
  id: 'cyclist-p3-s2-mobile',
  title: 'The Distracted Driver',
  description: 'Approaching a car with a driver using their phone.',
  phase: 3,
  role: 'cyclist',
  initialNodeId: 'start',
  learningObjectives: [
    'Recognize signs of distracted drivers',
    'Learn defensive positioning strategies',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You notice a car ahead on Murree Road is weaving slightly. As you get closer, you see the driver looking down at their phone. You need to pass them. What do you do?',
        options: [
          { id: 'pass-close', label: 'Pass normally in the bike lane', description: 'Stay in your lane and pass' },
          { id: 'pass-wide', label: 'Move into traffic lane to pass wide', description: 'Give maximum distance' },
          { id: 'slow-behind', label: 'Stay behind until they stop using the phone', description: 'Keep distance and wait' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'close-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Sudden Swerve',
        description: 'Just as you passed, the driver looked up and jerked the wheel. You were clipped by their mirror.',
        principle: 'Distracted drivers are unpredictable. The phone means their attention isn\'t on the road or their mirrors.',
      },
    },
    {
      id: 'wide-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Safe Buffer',
        description: 'You checked for traffic and moved wide. When the car swerved, you had plenty of space.',
        principle: 'When you spot a distracted driver, extra distance is your safety margin. Check traffic and give them wide berth.',
      },
    },
    {
      id: 'behind-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Patience Wins',
        description: 'After a minute, the car stopped at a light. You then passed safely while they were stationary.',
        principle: 'Sometimes the safest option is to wait for a better opportunity to pass.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'close-result', decisionId: 'pass-close', immediateFeedback: { severity: 'warning', title: 'Passing Close', description: 'You pass in the bike lane...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'wide-result', decisionId: 'pass-wide', immediateFeedback: { severity: 'positive', title: 'Moving Wide', description: 'You check and swing out...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'behind-result', decisionId: 'slow-behind', immediateFeedback: { severity: 'neutral', title: 'Staying Back', description: 'You slow and follow...' } },
  ],
};

const cyclist3_3: Scenario = {
  id: 'cyclist-p3-s3-emergency',
  title: 'The Emergency Stop',
  description: 'A child runs into the road ahead of you.',
  phase: 3,
  role: 'cyclist',
  initialNodeId: 'start',
  learningObjectives: [
    'Master emergency braking technique',
    'Understand how to avoid obstacles safely',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'residential-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re cycling through a residential area in Multan. Suddenly, a child runs out from between parked cars, directly in front of you. You have seconds to react. What do you do?',
        options: [
          { id: 'brake-hard', label: 'Brake as hard as possible', description: 'Maximum braking both wheels' },
          { id: 'swerve-left', label: 'Swerve left into the road', description: 'Try to go around the child' },
          { id: 'brake-front', label: 'Apply front brake firmly, rear lightly', description: 'Controlled emergency stop' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'hard-result',
      sceneConfig: { backgroundId: 'residential-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Rear Wheel Skid',
        description: 'Your rear wheel locked and skidded. You stopped in time but fell sideways. Minor scrapes but no collision.',
        principle: 'Hard braking on the rear wheel causes skids. It\'s better than hitting the child, but technique matters.',
      },
    },
    {
      id: 'swerve-result',
      sceneConfig: { backgroundId: 'residential-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Wrong Direction',
        description: 'You swerved left, but so did the startled child. You collided despite your evasive action.',
        principle: 'Children are unpredictable. Swerving around them is risky because you can\'t predict which way they\'ll move.',
      },
    },
    {
      id: 'front-result',
      sceneConfig: { backgroundId: 'residential-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Controlled Stop',
        description: 'You applied the front brake firmly while feathering the rear. You stopped quickly and in control, just short of the child.',
        principle: 'The front brake provides most stopping power. Use it firmly but not locked, with light rear brake to prevent skid.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'hard-result', decisionId: 'brake-hard', immediateFeedback: { severity: 'warning', title: 'Slamming Brakes', description: 'You grab both brakes hard...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'swerve-result', decisionId: 'swerve-left', immediateFeedback: { severity: 'warning', title: 'Swerving', description: 'You jerk left...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'front-result', decisionId: 'brake-front', immediateFeedback: { severity: 'positive', title: 'Controlled Braking', description: 'You apply brakes with technique...' } },
  ],
};

// ============================================================================
// DRIVER SCENARIOS - PHASE 1: FOUNDATION
// ============================================================================

const driver1_1: Scenario = {
  id: 'driver-p1-s1-pedestrian',
  title: 'The Pedestrian Crossing',
  description: 'Approaching a pedestrian waiting to cross.',
  phase: 1,
  role: 'driver',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand pedestrian priority at crossings',
    'Learn to anticipate pedestrian behavior',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re driving on Jail Road, Lahore. A pedestrian is standing at a zebra crossing, looking at you. Traffic behind you is close. What do you do?',
        options: [
          { id: 'slow-stop', label: 'Slow down and stop for them', description: 'Give way to the pedestrian' },
          { id: 'wave-through', label: 'Wave them across while slowing', description: 'Signal them to cross quickly' },
          { id: 'continue', label: 'Continue as they haven\'t stepped out', description: 'They\'re not on the crossing yet' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'stop-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Safe Stop',
        description: 'You stopped smoothly. The car behind stopped too. The pedestrian crossed safely and nodded thanks.',
        principle: 'At zebra crossings, pedestrians waiting to cross have priority. Stopping early signals your intentions to everyone.',
      },
    },
    {
      id: 'wave-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Mixed Signals',
        description: 'You waved them across, but the car behind didn\'t see your signal and nearly hit you when you stopped.',
        principle: 'Waving pedestrians across can create confusion. Just stop clearly and let them decide when it\'s safe.',
      },
    },
    {
      id: 'continue-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Failed Priority',
        description: 'The pedestrian assumed you would stop and stepped out. You had to brake hard, startling everyone.',
        principle: 'When someone is waiting at a zebra crossing, they expect traffic to stop. Not stopping creates dangerous confusion.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'stop-result', decisionId: 'slow-stop', immediateFeedback: { severity: 'positive', title: 'Slowing', description: 'You brake smoothly...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'wave-result', decisionId: 'wave-through', immediateFeedback: { severity: 'neutral', title: 'Waving', description: 'You gesture to them...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'continue-result', decisionId: 'continue', immediateFeedback: { severity: 'warning', title: 'Continuing', description: 'You maintain speed...' } },
  ],
};

const driver1_2: Scenario = {
  id: 'driver-p1-s2-mirror-check',
  title: 'The Lane Change',
  description: 'Changing lanes in moderate traffic.',
  phase: 1,
  role: 'driver',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand the importance of mirror and shoulder checks',
    'Learn proper lane change procedure',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'highway-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re on the motorway near Islamabad. You need to change to the right lane to exit. You check your mirror and see a gap. What do you do?',
        options: [
          { id: 'mirror-signal-move', label: 'Signal and move into the gap', description: 'Mirror shows it\'s clear' },
          { id: 'mirror-shoulder-signal', label: 'Check shoulder, then signal and move', description: 'Look over your shoulder first' },
          { id: 'signal-wait', label: 'Signal and wait for the car to pass', description: 'Let the visible car go first' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'mirror-result',
      sceneConfig: { backgroundId: 'highway-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Blind Spot',
        description: 'A motorcycle was in your blind spot. They had to swerve and brake hard to avoid you.',
        principle: 'Mirrors don\'t show everything. Always check your blind spot with a shoulder check before changing lanes.',
      },
    },
    {
      id: 'shoulder-result',
      sceneConfig: { backgroundId: 'highway-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Complete Check',
        description: 'Your shoulder check revealed a motorcycle. You waited for it to pass, then changed lanes safely.',
        principle: 'The shoulder check is essential. It reveals vehicles hidden in your mirror\'s blind spot.',
      },
    },
    {
      id: 'wait-result',
      sceneConfig: { backgroundId: 'highway-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Patient Approach',
        description: 'You waited for the visible car to pass. The lane was then completely clear for a safe change.',
        principle: 'When in doubt, wait. Missing an exit is inconvenient; a collision is dangerous.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'mirror-result', decisionId: 'mirror-signal-move', immediateFeedback: { severity: 'warning', title: 'Moving', description: 'You signal and start moving...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'shoulder-result', decisionId: 'mirror-shoulder-signal', immediateFeedback: { severity: 'positive', title: 'Checking', description: 'You glance over your shoulder...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'wait-result', decisionId: 'signal-wait', immediateFeedback: { severity: 'neutral', title: 'Waiting', description: 'You signal and wait...' } },
  ],
};

const driver1_3: Scenario = {
  id: 'driver-p1-s3-following',
  title: 'The Following Distance',
  description: 'Following traffic on a busy road.',
  phase: 1,
  role: 'driver',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand safe following distances',
    'Learn to anticipate sudden stops',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re following a car on GT Road. Traffic is moving at 60 km/h. A car tries to squeeze in from the left. If you maintain your gap, they\'ll get in. What do you do?',
        options: [
          { id: 'close-gap', label: 'Close the gap to prevent them', description: 'Don\'t let them push in' },
          { id: 'maintain-gap', label: 'Maintain your following distance', description: 'Let them merge if they want' },
          { id: 'flash-horn', label: 'Flash lights and horn to warn them', description: 'Let them know you\'re there' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'close-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Rear Collision',
        description: 'The car ahead braked suddenly. With no gap, you rear-ended them.',
        principle: 'Following distance isn\'t about other cars merging - it\'s your safety margin. Closing gaps makes you unsafe.',
      },
    },
    {
      id: 'maintain-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Safe Following',
        description: 'The car merged in. You eased back to restore your gap. When they braked suddenly later, you stopped safely.',
        principle: 'Let others merge. Your following distance protects you, not your lane position.',
      },
    },
    {
      id: 'flash-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Road Rage Trigger',
        description: 'The driver took offense and brake-checked you. The situation escalated unnecessarily.',
        principle: 'Aggressive signals create conflict. Stay calm and maintain safe distances rather than "defending" your space.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'close-result', decisionId: 'close-gap', immediateFeedback: { severity: 'warning', title: 'Closing Gap', description: 'You accelerate to close the gap...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'maintain-result', decisionId: 'maintain-gap', immediateFeedback: { severity: 'positive', title: 'Maintaining Distance', description: 'You keep your gap...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'flash-result', decisionId: 'flash-horn', immediateFeedback: { severity: 'warning', title: 'Signaling', description: 'You flash and horn...' } },
  ],
};

// ============================================================================
// DRIVER SCENARIOS - PHASE 2: COMPLEXITY
// ============================================================================

const driver2_1: Scenario = {
  id: 'driver-p2-s1-cyclist',
  title: 'The Cyclist Ahead',
  description: 'Approaching a cyclist on a narrow road.',
  phase: 2,
  role: 'driver',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand safe passing distances for cyclists',
    'Learn patience when overtaking is unsafe',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re driving on a narrow road in Peshawar. A cyclist is ahead. Oncoming traffic makes passing difficult. What do you do?',
        options: [
          { id: 'pass-close', label: 'Pass with minimal gap', description: 'Squeeze past when there\'s a small gap' },
          { id: 'wait-clear', label: 'Wait until road is clear', description: 'Follow until you can pass safely' },
          { id: 'horn-pass', label: 'Horn to warn, then pass', description: 'Alert the cyclist and overtake' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'close-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Too Close',
        description: 'The cyclist wobbled avoiding a pothole. With your small gap, you clipped them with your mirror.',
        principle: 'Cyclists need at least 1.5 meters clearance. They may swerve to avoid hazards you can\'t see.',
      },
    },
    {
      id: 'wait-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Patient Passing',
        description: 'You waited 30 seconds. When the road cleared, you passed with plenty of room. Safe and stress-free.',
        principle: 'A few seconds of patience costs nothing. A collision with a cyclist can change lives forever.',
      },
    },
    {
      id: 'horn-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Startled Cyclist',
        description: 'Your horn startled the cyclist. They swerved and nearly fell. You passed but created a dangerous situation.',
        principle: 'Horning at cyclists is intimidating and can cause them to lose control. Wait for safe passing room instead.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'close-result', decisionId: 'pass-close', immediateFeedback: { severity: 'warning', title: 'Squeezing', description: 'You move to pass close...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'wait-result', decisionId: 'wait-clear', immediateFeedback: { severity: 'neutral', title: 'Following', description: 'You match their speed and wait...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'horn-result', decisionId: 'horn-pass', immediateFeedback: { severity: 'warning', title: 'Horning', description: 'You sound your horn...' } },
  ],
};

const driver2_2: Scenario = {
  id: 'driver-p2-s2-junction',
  title: 'The Busy Junction',
  description: 'Turning right at a busy junction without signals.',
  phase: 2,
  role: 'driver',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand gap acceptance at junctions',
    'Learn to judge speeds and distances',
  ],
  estimatedDuration: 150,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You want to turn right at an uncontrolled junction in Quetta. Traffic is busy but you see a gap. A car is approaching but seems far. What do you do?',
        options: [
          { id: 'take-gap', label: 'Take the gap and turn', description: 'The approaching car is far enough' },
          { id: 'wait-gap', label: 'Wait for a clearer gap', description: 'Let this car pass first' },
          { id: 'edge-out', label: 'Edge out to see better', description: 'Move forward for a better view' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'take-result',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Misjudged Speed',
        description: 'The approaching car was faster than you thought. They braked hard and swerved, narrowly avoiding you.',
        principle: 'Judging approach speed is difficult. If you\'re not certain the gap is safe, it probably isn\'t.',
      },
    },
    {
      id: 'wait-result',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Safe Wait',
        description: 'The car passed quickly - faster than it looked. A clear gap came shortly after and you turned safely.',
        principle: 'When unsure about a gap, wait. A clear opportunity will come. Seconds of patience prevent accidents.',
      },
    },
    {
      id: 'edge-result',
      sceneConfig: { backgroundId: 'urban-intersection-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Committed Position',
        description: 'Edging out helped you see, but now you\'re blocking traffic. You felt pressured and took a risky gap.',
        principle: 'Only edge out if you can still wait safely. Being partly in the junction creates pressure to take unsafe gaps.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'take-result', decisionId: 'take-gap', immediateFeedback: { severity: 'warning', title: 'Going', description: 'You pull out...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'wait-result', decisionId: 'wait-gap', immediateFeedback: { severity: 'neutral', title: 'Waiting', description: 'You let them pass...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'edge-result', decisionId: 'edge-out', immediateFeedback: { severity: 'neutral', title: 'Edging', description: 'You move forward slowly...' } },
  ],
};

const driver2_3: Scenario = {
  id: 'driver-p2-s3-weather',
  title: 'The Rain Storm',
  description: 'Driving in heavy rain with reduced visibility.',
  phase: 2,
  role: 'driver',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand stopping distances in wet conditions',
    'Learn to adjust driving for weather',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'highway-rain', timeOfDay: 'day', weather: 'rain', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'Heavy rain starts while you\'re driving on the motorway near Hyderabad. Visibility drops significantly. Traffic is still moving at speed. What do you do?',
        options: [
          { id: 'maintain-speed', label: 'Maintain speed with traffic', description: 'Keep with the flow to avoid being hit from behind' },
          { id: 'slow-lights', label: 'Slow down and turn on hazard lights', description: 'Reduce speed and warn others' },
          { id: 'pull-over', label: 'Pull over and wait it out', description: 'Stop until visibility improves' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'maintain-result',
      sceneConfig: { backgroundId: 'highway-rain', timeOfDay: 'day', weather: 'rain', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Aquaplaning',
        description: 'At high speed, your car aquaplaned over standing water. You lost control briefly and hit the barrier.',
        principle: 'Wet roads dramatically increase stopping distances and reduce grip. Speed must be reduced for conditions.',
      },
    },
    {
      id: 'slow-result',
      sceneConfig: { backgroundId: 'highway-rain', timeOfDay: 'day', weather: 'rain', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Adjusted Speed',
        description: 'You slowed to a safe speed. Others followed your lead. When someone ahead braked suddenly, you stopped in time.',
        principle: 'In heavy rain, reduce speed by at least 30%. Use hazard lights if significantly slower than normal traffic.',
      },
    },
    {
      id: 'pullover-result',
      sceneConfig: { backgroundId: 'highway-rain', timeOfDay: 'day', weather: 'rain', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Roadside Risk',
        description: 'You pulled over safely, but staying on the hard shoulder in poor visibility is risky. A truck passed very close.',
        principle: 'If you must stop, find a service area or exit. The hard shoulder is dangerous in low visibility.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'maintain-result', decisionId: 'maintain-speed', immediateFeedback: { severity: 'warning', title: 'Keeping Speed', description: 'You continue at speed...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'slow-result', decisionId: 'slow-lights', immediateFeedback: { severity: 'positive', title: 'Slowing', description: 'You ease off and turn on hazards...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'pullover-result', decisionId: 'pull-over', immediateFeedback: { severity: 'neutral', title: 'Stopping', description: 'You pull onto the hard shoulder...' } },
  ],
};

// ============================================================================
// DRIVER SCENARIOS - PHASE 3: INTEGRATION
// ============================================================================

const driver3_1: Scenario = {
  id: 'driver-p3-s1-school',
  title: 'The School Zone',
  description: 'Driving through a school zone during pickup time.',
  phase: 3,
  role: 'driver',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand vulnerable road user awareness',
    'Learn to expect the unexpected near schools',
  ],
  estimatedDuration: 150,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'school-zone-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re driving past a school in Sialkot at 3 PM. Children and parents are everywhere. A ball rolls onto the road. What do you do?',
        options: [
          { id: 'brake-stop', label: 'Brake immediately and stop', description: 'A child might follow the ball' },
          { id: 'swerve-ball', label: 'Swerve around the ball', description: 'Avoid it without stopping' },
          { id: 'slow-watch', label: 'Slow down and watch for children', description: 'Reduce speed but keep moving' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'brake-result',
      sceneConfig: { backgroundId: 'school-zone-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Anticipation Saved',
        description: 'A child ran out for the ball just as you stopped. Your immediate reaction prevented a tragedy.',
        principle: 'Near schools, a ball on the road often means a child will follow. Always assume they will and stop immediately.',
      },
    },
    {
      id: 'swerve-result',
      sceneConfig: { backgroundId: 'school-zone-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Wrong Decision',
        description: 'You swerved around the ball. A child ran out at the same moment. Your swerve took you directly into their path.',
        principle: 'Swerving is unpredictable. Near schools, stop. The danger isn\'t the ball - it\'s the child who will chase it.',
      },
    },
    {
      id: 'slow-result',
      sceneConfig: { backgroundId: 'school-zone-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Too Slow to React',
        description: 'A child ran out. You were moving slowly but still had to brake hard. You stopped just in time.',
        principle: 'Slowing helps but a ball near a school requires a full stop. Children can appear in an instant.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'brake-result', decisionId: 'brake-stop', immediateFeedback: { severity: 'positive', title: 'Stopping', description: 'You brake hard and stop...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'swerve-result', decisionId: 'swerve-ball', immediateFeedback: { severity: 'warning', title: 'Swerving', description: 'You steer around the ball...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'slow-result', decisionId: 'slow-watch', immediateFeedback: { severity: 'warning', title: 'Slowing', description: 'You reduce speed and watch...' } },
  ],
};

const driver3_2: Scenario = {
  id: 'driver-p3-s2-emergency',
  title: 'The Ambulance',
  description: 'An ambulance approaches while you\'re in traffic.',
  phase: 3,
  role: 'driver',
  initialNodeId: 'start',
  learningObjectives: [
    'Understand emergency vehicle protocols',
    'Learn safe ways to create space',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'re in traffic in Gujranwala when you hear a siren. An ambulance is approaching from behind. There\'s a car beside you and limited space. What do you do?',
        options: [
          { id: 'mount-curb', label: 'Mount the curb if safe', description: 'Create space by moving onto the pavement edge' },
          { id: 'stop-lane', label: 'Stop in your lane', description: 'Just stop and let them find a way through' },
          { id: 'speed-gap', label: 'Speed up to find a gap ahead', description: 'Move forward to clear space behind you' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'curb-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Space Created',
        description: 'You safely mounted the low curb. Combined with the car beside you moving left, the ambulance passed through quickly.',
        principle: 'Creating space for emergency vehicles is everyone\'s responsibility. If safe, mounting a low curb is acceptable.',
      },
    },
    {
      id: 'stop-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Blocked Path',
        description: 'Just stopping didn\'t create space. The ambulance had to wait for other cars to move. Precious seconds were lost.',
        principle: 'Don\'t just stop - actively create space if you can. Emergency vehicles need a clear path, not just stopped traffic.',
      },
    },
    {
      id: 'speed-result',
      sceneConfig: { backgroundId: 'urban-road-day', timeOfDay: 'day', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Unpredictable',
        description: 'Speeding up made your movements unpredictable. Other drivers didn\'t know what you were doing, creating confusion.',
        principle: 'Don\'t speed up for emergency vehicles. Slow, pull left, and stop. Predictable movements help everyone coordinate.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'curb-result', decisionId: 'mount-curb', immediateFeedback: { severity: 'positive', title: 'Moving Left', description: 'You carefully mount the curb...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'stop-result', decisionId: 'stop-lane', immediateFeedback: { severity: 'neutral', title: 'Stopping', description: 'You stop in your lane...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'speed-result', decisionId: 'speed-gap', immediateFeedback: { severity: 'warning', title: 'Speeding', description: 'You accelerate forward...' } },
  ],
};

const driver3_3: Scenario = {
  id: 'driver-p3-s3-fatigue',
  title: 'The Long Drive',
  description: 'Driving late at night on a highway journey.',
  phase: 3,
  role: 'driver',
  initialNodeId: 'start',
  learningObjectives: [
    'Recognize fatigue warning signs',
    'Learn safe strategies for long journeys',
  ],
  estimatedDuration: 120,
  nodes: [
    {
      id: 'start',
      sceneConfig: { backgroundId: 'highway-night', timeOfDay: 'night', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: {
        id: 'dp-1',
        prompt: 'You\'ve been driving for 4 hours on the motorway at night. You catch yourself yawning and your eyes feel heavy. Your destination is 1 hour away. What do you do?',
        options: [
          { id: 'push-through', label: 'Push through, almost there', description: 'Open the window, play loud music' },
          { id: 'pull-rest', label: 'Pull over and rest', description: 'Find a safe spot and take a break' },
          { id: 'coffee-stop', label: 'Stop for coffee and stretch', description: 'Quick service station stop' },
        ],
      },
      consequenceFlags: [],
      isTerminal: false,
    },
    {
      id: 'push-result',
      sceneConfig: { backgroundId: 'highway-night', timeOfDay: 'night', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'negative',
        title: 'Micro-Sleep',
        description: 'You experienced a micro-sleep. You drifted across lanes and woke as your tires hit the rumble strip.',
        principle: 'Fatigue is as dangerous as alcohol. When you feel tired, no amount of fresh air or loud music will keep you safe.',
      },
    },
    {
      id: 'rest-result',
      sceneConfig: { backgroundId: 'highway-night', timeOfDay: 'night', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'positive',
        title: 'Smart Stop',
        description: 'You found a service area and napped for 20 minutes. You woke refreshed and completed the journey safely.',
        principle: 'A 20-minute nap is the most effective fatigue countermeasure. It\'s always worth the time.',
      },
    },
    {
      id: 'coffee-result',
      sceneConfig: { backgroundId: 'highway-night', timeOfDay: 'night', weather: 'clear', actors: [], signals: [], overlays: [] },
      decisionPoint: null,
      consequenceFlags: [],
      isTerminal: true,
      feedback: {
        severity: 'warning',
        title: 'Temporary Relief',
        description: 'The coffee helped briefly, but fatigue returned. You arrived safely but were more tired than before the coffee.',
        principle: 'Caffeine takes 20-30 minutes to work and masks fatigue rather than curing it. A nap is more effective.',
      },
    },
  ],
  edges: [
    { id: 'e1', fromNodeId: 'start', toNodeId: 'push-result', decisionId: 'push-through', immediateFeedback: { severity: 'warning', title: 'Pushing On', description: 'You turn up the music...' } },
    { id: 'e2', fromNodeId: 'start', toNodeId: 'rest-result', decisionId: 'pull-rest', immediateFeedback: { severity: 'positive', title: 'Resting', description: 'You find a service area...' } },
    { id: 'e3', fromNodeId: 'start', toNodeId: 'coffee-result', decisionId: 'coffee-stop', immediateFeedback: { severity: 'neutral', title: 'Coffee Break', description: 'You stop for coffee...' } },
  ],
};

// ============================================================================
// SCENARIO REGISTRY
// ============================================================================

import type { UserRole } from '@core/types';

/**
 * All scenarios organized by role and phase
 */
const SCENARIOS_BY_ROLE: Record<UserRole, Record<Phase, Scenario[]>> = {
  pedestrian: {
    1: [scenario1_1, scenario1_2, scenario1_3],
    2: [scenario2_1, scenario2_2, scenario2_3],
    3: [scenario3_1, scenario3_2, scenario3_3],
  },
  cyclist: {
    1: [cyclist1_1, cyclist1_2, cyclist1_3],
    2: [cyclist2_1, cyclist2_2, cyclist2_3],
    3: [cyclist3_1, cyclist3_2, cyclist3_3],
  },
  driver: {
    1: [driver1_1, driver1_2, driver1_3],
    2: [driver2_1, driver2_2, driver2_3],
    3: [driver3_1, driver3_2, driver3_3],
  },
};

/**
 * Get scenarios for a specific phase and role
 */
export function getScenariosForPhase(phase: Phase, role?: UserRole): Scenario[] {
  if (role) {
    return SCENARIOS_BY_ROLE[role]?.[phase] || [];
  }
  // If no role specified, return pedestrian scenarios for backward compatibility
  return SCENARIOS_BY_ROLE.pedestrian[phase] || [];
}

/**
 * Get a specific scenario by ID
 */
export function getScenarioById(id: string): Scenario | undefined {
  for (const role of Object.values(SCENARIOS_BY_ROLE)) {
    for (const phase of Object.values(role)) {
      const scenario = phase.find((s) => s.id === id);
      if (scenario) return scenario;
    }
  }
  return undefined;
}

/**
 * Get all scenarios
 */
export function getAllScenarios(): Scenario[] {
  const all: Scenario[] = [];
  for (const role of Object.values(SCENARIOS_BY_ROLE)) {
    for (const phase of Object.values(role)) {
      all.push(...phase);
    }
  }
  return all;
}

/**
 * Get total scenario count
 */
export function getTotalScenarioCount(): number {
  return getAllScenarios().length;
}

/**
 * Get scenario count by role
 */
export function getScenarioCountByRole(role: UserRole): number {
  const roleScenarios = SCENARIOS_BY_ROLE[role];
  if (!roleScenarios) return 0;
  return Object.values(roleScenarios).reduce((sum, arr) => sum + arr.length, 0);
}
